/**
 * 四门户认证 API 工厂（FE-E2b 下沉）：login / logout / refreshTokens / fetchMe /
 * fetchMenus / refreshSessionMe / logoutLocal 模板化；端差异（mockMe、基础会话构造、
 * 登录后 me/menus 合并、作用域 id 回写、偏好联动钩子）经 CreateAuthApiConfig 参数化。
 *
 * 行为等价 = FE-E2b 前各端 src/api/auth.ts 镜像结构：
 * - login 成功 → saveSession(基础会话) → （可选钩子）→ 并行拉 me/menus → 合并写回；
 *   失败不回滚（已登录，UI 按降级展示）
 * - logout 无论后端成败均 signOutLocal（不卡死）
 * - refreshTokens 走 POST /auth/refresh + skipAuth（不触发 401 重放）
 * 本模块不 import 任何门户代码（verify-p2-boundary）。
 */

// ===================== 公共类型 =====================

export type TokenPair = {
  accessToken: string;
  refreshToken: string;
  tokenType?: string;
  expiresIn?: number;
  permissions?: string[];
};

/** 模板所见的 API 结果最小面（与四端 ApiResult 结构兼容） */
type AuthApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; code?: string; traceId?: string; status?: number };

/** 菜单节点（对齐后端 MenuNodeView record） */
type AuthMenuNodeView = {
  id: string;
  title: string;
  path?: string | null;
  permission?: string | null;
  children?: AuthMenuNodeView[];
};

interface CreateAuthApiConfig<S, V> {
  /** 端 get/post（fetchMe/fetchMenus 用 get，login/refresh/logout 用 post；opts 兼容四端形态） */
  get: <T>(
    path: string,
    mock: () => T,
    opts?: { skipAuth?: boolean; headers?: Record<string, string> },
  ) => Promise<AuthApiResult<T>>;
  post: <T>(
    path: string,
    body: unknown,
    mock: () => T,
    opts?: { skipAuth?: boolean; headers?: Record<string, string> },
  ) => Promise<AuthApiResult<T>>;
  loadSession: () => S | null;
  saveSession: (s: S) => void;
  clearSession: () => void;
  signOutLocal: () => void;
  /** fetchMe 的 mock 数据（端特定：渠道/作用域 id/守卫标志） */
  mockMe: () => V;
  /** 登录成功后基础会话构造（token 三元组 + principal；customer 可读旧会话取 cardholderId） */
  buildSession: (t: TokenPair, principal: string) => S;
  /** 并行 me/menus 拉取后的合并写回（端作用域 id 差异在此表达；拉取失败不回滚） */
  mergeAfterLogin: (
    current: S,
    meRes: AuthApiResult<V>,
    menusRes: AuthApiResult<AuthMenuNodeView[]>,
  ) => S;
  /** 登录成功后（基础会话写入后、并行拉取前）的 fire-and-forget 钩子（customer 偏好联动） */
  afterLogin?: () => void;
  /** refreshSessionMe 成功时的附加回写（agent/merchant 作用域 id；缺省仅写 me） */
  applyMe?: (current: S, me: V) => S;
}

// ===================== 工厂实现 =====================

export function createAuthApi<S, V extends Record<string, unknown>>(cfg: CreateAuthApiConfig<S, V>) {
  const { get, post } = cfg;

  /** AUTH-004：当前会话身份（GET /auth/me） */
  function fetchMe(): Promise<AuthApiResult<V>> {
    return get<V>('/auth/me', cfg.mockMe);
  }

  /** 用最新 /auth/me 覆盖 session.me（绑 2FA 后刷新守卫标志） */
  async function refreshSessionMe(): Promise<V | null> {
    const res = await fetchMe();
    const current = cfg.loadSession();
    if (!current) {
      return null;
    }
    if (!res.ok) {
      return ((current as { me?: V | null }).me) ?? null;
    }
    cfg.saveSession(cfg.applyMe ? cfg.applyMe(current, res.data) : { ...current, me: res.data });
    return res.data;
  }

  /** AUTH-005：可见菜单（GET /auth/menus，已按 permissions 过滤） */
  function fetchMenus(): Promise<AuthApiResult<AuthMenuNodeView[]>> {
    return get<AuthMenuNodeView[]>('/auth/menus', () => []);
  }

  /** 续期（POST /auth/refresh）；skipAuth，不触发 401 重放 */
  function refreshTokens(refreshToken: string): Promise<AuthApiResult<TokenPair>> {
    return post<TokenPair>(
      '/auth/refresh',
      { refreshToken },
      () => ({
        accessToken: 'mock-access-refreshed',
        refreshToken: 'mock-refresh-refreshed',
        tokenType: 'Bearer',
        expiresIn: 3600,
        permissions: [],
      }),
      { skipAuth: true },
    );
  }

  /** 登出（POST /auth/logout，带 Bearer）；成功 / 失败均清会话并跳登录（不卡死） */
  async function logout(): Promise<AuthApiResult<void>> {
    const res = await post<void>('/auth/logout', undefined, () => undefined);
    // 无论后端是否成功，本地都清会话 + 跳登录；失败也兜底
    cfg.signOutLocal();
    return res;
  }

  /**
   * 登录（POST /auth/login，skipAuth）。成功后并行拉 me / menus 写入会话；
   * 作用域 id 合并经 mergeAfterLogin 表达（agent=agentId / merchant=merchantId /
   * customer=cardholderId 纠正；admin 无作用域 id）。
   */
  async function login(principal: string, password: string, otp?: string): Promise<AuthApiResult<TokenPair>> {
    const res = await post<TokenPair>(
      '/auth/login',
      { principal, password, ...(otp ? { otp } : {}) },
      () => ({
        accessToken: 'mock-access',
        refreshToken: 'mock-refresh',
        tokenType: 'Bearer',
        expiresIn: 3600,
        permissions: [],
      }),
      { skipAuth: true },
    );
    if (res.ok) {
      cfg.saveSession(cfg.buildSession(res.data, principal));
      cfg.afterLogin?.();
      // 并行拉取 me / menus 写入会话；失败不回滚登录态（已登录，UI 按降级展示）
      const [meRes, menusRes] = await Promise.all([fetchMe(), fetchMenus()]);
      const current = cfg.loadSession();
      if (current) {
        cfg.saveSession(cfg.mergeAfterLogin(current, meRes, menusRes));
      }
    }
    return res;
  }

  /** 仅清本地会话，不触发跳登录回调（向后兼容；主动登出请用 logout） */
  function logoutLocal() {
    cfg.clearSession();
  }

  return { fetchMe, refreshSessionMe, fetchMenus, refreshTokens, logout, login, logoutLocal };
}
